const GRAPHICS_QUALITY_KEY = "aeternaInvicta.graphicsQuality";
const GRAPHICS_LEARNED_TIER_KEY = "aeternaInvicta.graphicsLearnedTier";
const GRAPHICS_LEARNED_TIER_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export const GRAPHICS_TIERS = ["high", "medium", "low"];
export const GRAPHICS_QUALITY_OPTIONS = ["auto", ...GRAPHICS_TIERS];

const TIER_RANK = { low: 0, medium: 1, high: 2 };

const SOFTWARE_GPU = /swiftshader|llvmpipe|softpipe|software|basic render/i;
const WEAK_GPU =
  /mali-[4t]|adreno[^0-9]*[2-4]\d\d(?!\d)|powervr sgx|\bgma\b|intel\(r\) hd graphics(?: [2-5]\d{3})?(?!\s*\d)/i;
const MID_GPU = /intel|mali|adreno|powervr|videocore|tegra/i;
const STRONG_INTEL_GPU = /iris\(r\) xe|iris xe|\barc\b/i;
const STRONG_MOBILE_GPU =
  /adreno[^0-9]*[7-9]\d\d(?!\d)|immortalis|xclipse|mali-g7[1-9]|mali-g[7-9]\d\d/i;

const WATCH_WARMUP_MS = 2500;
const WATCH_WINDOW_MS = 4000;
const WATCH_SLOW_FRAME_MS = 50;
const WATCH_SLOW_WINDOW_FPS = 42;
const WATCH_SLOW_WINDOW_RATIO = 0.12;
const WATCH_SLOW_WINDOWS_TO_DOWNGRADE = 2;

let cachedDeviceTier = null;

export function normalizeGraphicsTier(value) {
  return GRAPHICS_TIERS.includes(value) ? value : "high";
}

export function normalizeGraphicsQuality(value) {
  return GRAPHICS_TIERS.includes(value) ? value : "auto";
}

export function lowerGraphicsTier(tier) {
  if (tier === "high") return "medium";
  return "low";
}

function lowestGraphicsTier(...tiers) {
  return tiers
    .filter((tier) => GRAPHICS_TIERS.includes(tier))
    .reduce(
      (lowest, tier) => (TIER_RANK[tier] < TIER_RANK[lowest] ? tier : lowest),
      "high",
    );
}

export function loadGraphicsQuality() {
  try {
    return normalizeGraphicsQuality(
      window.localStorage.getItem(GRAPHICS_QUALITY_KEY),
    );
  } catch (err) {
    return "auto";
  }
}

export function saveGraphicsQuality(value) {
  try {
    window.localStorage.setItem(
      GRAPHICS_QUALITY_KEY,
      normalizeGraphicsQuality(value),
    );
  } catch (err) {}
}

export function loadLearnedGraphicsTier() {
  try {
    const raw = window.localStorage.getItem(GRAPHICS_LEARNED_TIER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !GRAPHICS_TIERS.includes(parsed.tier)) return null;
    if (Date.now() - (Number(parsed.at) || 0) > GRAPHICS_LEARNED_TIER_TTL_MS) {
      window.localStorage.removeItem(GRAPHICS_LEARNED_TIER_KEY);
      return null;
    }
    return parsed.tier;
  } catch (err) {
    return null;
  }
}

export function saveLearnedGraphicsTier(tier) {
  try {
    window.localStorage.setItem(
      GRAPHICS_LEARNED_TIER_KEY,
      JSON.stringify({ tier: normalizeGraphicsTier(tier), at: Date.now() }),
    );
  } catch (err) {}
}

export function clearLearnedGraphicsTier() {
  try {
    window.localStorage.removeItem(GRAPHICS_LEARNED_TIER_KEY);
  } catch (err) {}
}

function probeGpu() {
  const empty = { renderer: "", software: false, available: false };
  if (typeof document === "undefined") return empty;
  try {
    const canvas = document.createElement("canvas");
    let gl = canvas.getContext("webgl", { failIfMajorPerformanceCaveat: true });
    let software = false;
    if (!gl) {
      gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      software = Boolean(gl);
    }
    if (!gl) return empty;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = String(
      (info && gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) ||
        gl.getParameter(gl.RENDERER) ||
        "",
    );
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return { renderer, software, available: true };
  } catch (err) {
    return empty;
  }
}

function isAppleMobile(ua, nav) {
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (/macintosh/i.test(ua) && Number(nav.maxTouchPoints) > 1)
  );
}

function isMobileDevice(ua, nav) {
  if (nav.userAgentData?.mobile === true) return true;
  if (/android|iphone|ipad|ipod|mobile|silk|kindle/i.test(ua)) return true;
  return isAppleMobile(ua, nav);
}

function computeDeviceGraphicsTier() {
  if (typeof navigator === "undefined") return "high";
  const nav = navigator;
  const ua = String(nav.userAgent || "");
  const cores = Number(nav.hardwareConcurrency) || 0;
  const memory = Number(nav.deviceMemory) || 0;
  const appleMobile = isAppleMobile(ua, nav);
  if (nav.connection?.saveData === true) return "low";
  if (memory && memory <= 2) return "low";
  if (!appleMobile && cores && cores <= 2) return "low";
  const gpu = probeGpu();
  if (gpu.software || SOFTWARE_GPU.test(gpu.renderer)) return "low";
  if (WEAK_GPU.test(gpu.renderer)) return "low";
  if (isMobileDevice(ua, nav)) {
    if (appleMobile) return "medium";
    if (memory >= 8 && cores >= 8 && STRONG_MOBILE_GPU.test(gpu.renderer)) {
      return "high";
    }
    if ((cores && cores <= 4) || (memory && memory <= 3)) return "low";
    return "medium";
  }
  if (!gpu.available) return "medium";
  if ((cores && cores <= 4) || (memory && memory <= 4)) return "medium";
  if (MID_GPU.test(gpu.renderer) && !STRONG_INTEL_GPU.test(gpu.renderer)) {
    return "medium";
  }
  return "high";
}

export function detectDeviceGraphicsTier() {
  if (!cachedDeviceTier) cachedDeviceTier = computeDeviceGraphicsTier();
  return cachedDeviceTier;
}

export function resolveAutoGraphicsTier() {
  return lowestGraphicsTier(
    detectDeviceGraphicsTier(),
    loadLearnedGraphicsTier(),
  );
}

export function applyGraphicsTierToDocument(tier) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(
    "data-ai-graphics",
    normalizeGraphicsTier(tier),
  );
}

export function capFoilQualityForGraphics(quality, tier) {
  if (tier === "high" || quality !== "maximum") return quality;
  return "minimum";
}

export function watchFrameRate(onSustainedSlow) {
  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    return () => {};
  }
  let raf = 0;
  let stopped = false;
  let startedAt = 0;
  let last = 0;
  let windowStart = 0;
  let frames = 0;
  let slowFrames = 0;
  let slowWindows = 0;

  const restart = () => {
    startedAt = 0;
    last = 0;
  };

  const tick = (now) => {
    if (stopped) return;
    if (!startedAt) {
      startedAt = now;
      last = now;
      windowStart = now;
      frames = 0;
      slowFrames = 0;
      raf = window.requestAnimationFrame(tick);
      return;
    }
    const delta = now - last;
    last = now;
    if (now - startedAt < WATCH_WARMUP_MS) {
      windowStart = now;
      raf = window.requestAnimationFrame(tick);
      return;
    }
    frames += 1;
    if (delta > WATCH_SLOW_FRAME_MS) slowFrames += 1;
    const elapsed = now - windowStart;
    if (elapsed >= WATCH_WINDOW_MS) {
      const fps = (frames * 1000) / elapsed;
      const slow =
        fps < WATCH_SLOW_WINDOW_FPS ||
        slowFrames / Math.max(1, frames) > WATCH_SLOW_WINDOW_RATIO;
      slowWindows = slow ? slowWindows + 1 : 0;
      windowStart = now;
      frames = 0;
      slowFrames = 0;
      if (slowWindows >= WATCH_SLOW_WINDOWS_TO_DOWNGRADE) {
        stopped = true;
        onSustainedSlow();
        return;
      }
    }
    raf = window.requestAnimationFrame(tick);
  };

  const handleVisibility = () => {
    restart();
    slowWindows = 0;
  };

  document.addEventListener("visibilitychange", handleVisibility);
  raf = window.requestAnimationFrame(tick);
  return () => {
    stopped = true;
    if (raf) window.cancelAnimationFrame(raf);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
}
